import { useEffect, useState } from "react";
import { PublicLayout } from "../../components/PublicLayout";
import { api } from "../../lib/api";

interface VoucherDetail {
  voucher_type: string;
  status: string;
  count: number;
}

interface InventorySummary {
  member: null | {
    member_account: string;
    status_label: string;
  };
  assets: {
    vouchers: number;
    voucher_details?: VoucherDetail[];
    gems: number;
    glory_value: number;
  };
  invite_status?: null | {
    is_unlocked: boolean;
  };
}

const voucherOrder = ["newbie_bonus_red_packet", "newbie_launch_red_packet", "graduate_launch_red_packet", "advanced_launch_red_packet", "weekly_bright_red_packet", "weekend_bright_red_packet", "weekend_advanced_red_packet", "weekly_glory", "premium_packet"];

export function InventoryPage() {
  const [summary, setSummary] = useState<InventorySummary | null>(null);
  const account = localStorage.getItem("current_member_account") ?? localStorage.getItem("memberAccount") ?? "";

  useEffect(() => {
    if (!account) return;
    api<InventorySummary>(`/api/member/summary?member_account=${encodeURIComponent(account)}`).then(setSummary).catch(() => undefined);
  }, [account]);

  const voucherDetails = summary?.assets.voucher_details ?? [];

  return (
    <PublicLayout title="會員背包" subtitle="查看寶石碎片、紅包券與會員資產">
      {summary?.member ? (
        <section className="inventory-panel">
          <span className="hero-kicker">會員背包</span>
          <h1>{summary.member.member_account} 的背包</h1>
          <div className="inventory-stat-grid">
            <div><span>會員狀態</span><strong>{summary.member.status_label}</strong></div>
            <div><span>寶石碎片</span><strong>{summary.assets.gems ?? 0}</strong></div>
            <div><span>可用紅包券</span><strong>{summary.assets.vouchers ?? 0}</strong></div>
            <div><span>閃耀值</span><strong>{summary.assets.glory_value ?? 0}</strong></div>
            <div><span>邀友支線</span><strong>{summary.invite_status?.is_unlocked ? "已開啟" : "尚未開啟"}</strong></div>
          </div>

          <div className="inventory-voucher-grid" aria-label="紅包券列表">
            {voucherOrder.map((type) => {
              const available = countVoucher(voucherDetails, type, "available");
              const used = countVoucher(voucherDetails, type, "used");
              const expired = countVoucher(voucherDetails, type, "expired");
              const hasAny = available + used + expired > 0;
              return (
                <article className={`inventory-voucher-card ${available > 0 ? "active" : hasAny ? "used" : "soon"}`} key={type}>
                  <span>{available > 0 ? "可使用" : hasAny ? "已取得紀錄" : "尚未取得"}</span>
                  <h3>{voucherTypeLabel(type)}</h3>
                  <strong>可用 {available} 張</strong>
                  <p>已使用 {used} 張，已過期 {expired} 張。</p>
                  {type === "newbie_bonus_red_packet" && available > 0 ? <a className="secondary-cta" href="/red-packet">前往紅包</a> : null}
                </article>
              );
            })}
          </div>

          <p className="admin-muted">紅包券本身不進派發中心，只有抽中河幣結果才會進入派發中心。</p>
          <a className="secondary-cta" href="/">返回大廳</a>
        </section>
      ) : (
        <section className="login-gate">
          <span className="hero-kicker">會員背包</span>
          <h1>請先登入龍來喬活動大廳</h1>
          <p>登入後即可查看你的背包、紅包券與寶石碎片。</p>
          <a className="primary-cta" href="/">返回登入</a>
        </section>
      )}
    </PublicLayout>
  );
}

function countVoucher(rows: VoucherDetail[], type: string, status: string) {
  return rows
    .filter((row) => row.voucher_type === type && row.status === status)
    .reduce((sum, row) => sum + Number(row.count ?? 0), 0);
}

function voucherTypeLabel(type: string) {
  if (type === "graduate_launch_red_packet") return "畢業啟航紅包券";
  if (type === "advanced_launch_red_packet") return "高級啟航紅包券";
  if (type === "graduate_start") return "畢業啟航紅包券（legacy）";
  if (type === "advanced_start") return "高級啟航紅包券（legacy）";
  if (type === "newbie_bonus_red_packet") return "新手加碼紅包券";
  if (type === "newbie_launch_red_packet") return "新人啟航紅包券";
  if (type === "weekly_bright_red_packet") return "週耀紅包券";
  if (type === "weekend_bright_red_packet") return "週末耀旺紅包券";
  if (type === "weekend_advanced_red_packet") return "高級週末紅包券";
  if (type === "weekly_glory") return "週耀紅包券（legacy）";
  if (type === "premium_packet") return "高級紅包券";
  return type;
}
